import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { EmptyState } from "@/components/shared";
import { RECON_TONE, fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";

/** The client file a figure was read from, kept quiet so it never competes with the number. */
const Docs = ({ names }) => {
  if (!names?.length) return null;
  return (
    <p className="mt-0.5 truncate text-[9px] font-normal text-muted-foreground/70"
       title={names.join(", ")}>
      {names.join(", ")}
    </p>
  );
};

export default function FpReconciliation() {
  const { tenantId, period, basis } = useFpContext();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/reconciliation`,
                                          { params: { period, basis } });
      setData(out);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  const differences = (data?.reconciliation || []).filter((r) => r.status === "Difference");
  const noControl = (data?.reconciliation || []).filter((r) => r.status === "No control total");

  return (
    <FpShell
      title="Reconciliation & control totals"
      description="Every allocated or interface total must agree to its accounting control total. Differences are flagged and traceable; an unreconciled area is never presented as validated Level 3."
    >
      {!period ? (
        <EmptyState testid="fp-recon-empty" title="No statements yet"
                    message="Upload a Trial Balance for a month first." />
      ) : data?.error ? (
        <EmptyState testid="fp-recon-error" title="Unavailable" message={data.error} />
      ) : !data ? (
        <p className="label-caps animate-pulse">Reconciling</p>
      ) : (
        <div className="space-y-6" data-testid="fp-reconciliation">
          <div className="panel p-6">
            <p className="label-caps">
              {data.fiscal_year} · {basis === "ytd" ? "cumulative" : "month"} ·
              {" "}{data.periods.map(monthLabel).join(", ")}
            </p>
            <p className={`mt-2 font-display text-2xl ${
              differences.length ? "text-destructive" : "text-success"}`} data-testid="fp-recon-headline">
              {differences.length
                ? `${differences.length} control item(s) do not reconcile`
                : "All allocated detail reconciles to the accounting control totals"}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Tolerance is configured by Aikyam under Administration → Financial engine configuration.
              The pilot default is exact reconciliation.
              {noControl.length > 0 && ` ${noControl.length} item(s) have no accounting control total and are excluded from the count.`}
            </p>
          </div>

          <div className="panel overflow-hidden" data-testid="fp-recon-table">
            <div className="grid grid-cols-1 gap-4 border-b border-border bg-muted/30 px-5 py-4 sm:grid-cols-2">
              <div>
                <p className="label-caps">Tally control total</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  The accounting truth. Every ledger in the client's Tally books — Trial Balance, or
                  the P&amp;L and Balance Sheet where no TB is supplied — routed through the mapping
                  master. It says <em>how much</em>.
                </p>
              </div>
              <div>
                <p className="label-caps">Interface detail total</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  The same figure rebuilt from the supplementary sheets — sales register, production,
                  distribution, payroll, expense allocation. These carry the outlet, category and
                  cost-component splits, so they say <em>where it came from</em>. Your expected
                  output packs are never used here.
                </p>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 text-left">Control item</th>
                  <th className="px-5 py-3 text-right">Tally control total</th>
                  <th className="px-5 py-3 text-right">Interface detail total</th>
                  <th className="px-5 py-3 text-right">Difference</th>
                  <th className="px-5 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.reconciliation.map((r) => (
                  <tr key={r.control_item}
                      data-testid={`fp-recon-${r.control_item.toLowerCase().replace(/\W+/g, "-")}`}>
                    <td className="px-5 py-3 font-medium text-navy">{r.control_item}</td>
                    <td className="px-5 py-3 text-right align-top">
                      <span className="font-mono tabular-nums text-navy">
                        {r.control_total === null ? "—" : inr(r.control_total)}
                      </span>
                      <Docs names={r.control_documents} />
                    </td>
                    <td className="px-5 py-3 text-right align-top">
                      <span className="font-mono tabular-nums text-foreground/80">
                        {r.detail_total === null ? "—" : inr(r.detail_total)}
                      </span>
                      <Docs names={r.detail_documents} />
                    </td>
                    <td className={`px-5 py-3 text-right align-top font-mono tabular-nums ${
                      r.difference ? "font-semibold text-destructive" : "text-success"}`}>
                      {r.difference === null ? "—" : inr(r.difference)}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${RECON_TONE[r.status]}`}>
                        {r.status}
                      </span>
                      {r.note && <p className="mt-1 max-w-md text-[10px] text-muted-foreground">{r.note}</p>}
                      {r.drivers?.length > 0 && (
                        <details className="mt-2" data-testid={`fp-recon-drivers-${r.control_item.toLowerCase().replace(/\W+/g, "-")}`}>
                          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.1em] text-terracotta">
                            What is driving this
                          </summary>
                          {r.probable_cross_entity?.length > 0 && (
                            <p className="mt-1.5 max-w-md text-[10px] text-[hsl(30_88%_28%)]">
                              Likely inter-entity or non-operating:{" "}
                              {r.probable_cross_entity.map((d) => d.ledger).join(", ")}
                            </p>
                          )}
                          {r.duplicate_ledgers?.length > 0 && (
                            <div className="mt-1.5 max-w-md text-[10px] text-[hsl(30_88%_28%)]">
                              <p>Same account under two names — inflates the books side:</p>
                              <ul className="mt-0.5">
                                {r.duplicate_ledgers.map((g, i) => (
                                  <li key={i}>
                                    {g.ledgers.join(" + ")}{" = "}
                                    <span className="font-mono tabular-nums">{inr(g.total)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <ul className="mt-1.5 space-y-0.5">
                            {r.drivers.map((d, i) => (
                              <li key={i} className="flex justify-between gap-4 text-[10px] text-muted-foreground">
                                <span className="truncate">{d.ledger}</span>
                                <span className="shrink-0 font-mono tabular-nums">{inr(d.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="panel p-6" data-testid="fp-recon-sources">
            <p className="label-caps">Sources used</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.sources.map((s) => (
                <div key={s.code} className="rounded-xl border border-border p-4">
                  <p className="text-sm font-semibold text-navy">{s.label}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {s.rows} rows · v{s.version} · {s.periods.map(monthLabel).join(", ")}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{s.uploaded_by}</p>
                </div>
              ))}
            </div>
            {data.sources_missing.length > 0 && (
              <p className="mt-4 text-[11px] text-muted-foreground" data-testid="fp-recon-missing-sources">
                Not supplied for this period: {data.sources_missing.map((s) => s.label).join(", ")}
              </p>
            )}
          </div>
        </div>
      )}
    </FpShell>
  );
}
